// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment */

import useHiroPolling, { useHiroPollingProps } from './useHiroPolling';
import { HiroUpdatingType, HiroLense, HiroOptionType } from '../../types';
import { useStreamingProps } from '../bloomberg';
import { useRecoilValue } from 'recoil';
import { hiroUpdatingTypeState, sumWindowState } from '../../states';
import useStreaming from '../bloomberg/streaming/useStreaming';
import { isZerohedge } from '../../util';
import { useTheme } from '@mui/material/styles';
import { CandlestickSeries, LineSeries } from 'lightweight-charts';

export const SERIES_DEFAULT_OPTS = {
  lineWidth: 2,
  type: LineSeries,
  ...(isZerohedge() ? { lineWidth: 1 } : {}),
};

const CANDLE_DEFAULT_SERIES_OPTS = {
  type: CandlestickSeries,
  wickUpColor: '#838ca1',
  wickDownColor: '#838ca1',
};

const CANDLE_RED_COLOR = '#cc304a';

const useHiroUpdating = (props?: useHiroPollingProps | useStreamingProps) => {
  const type = useRecoilValue(hiroUpdatingTypeState);
  const rollingSeconds = useRecoilValue(sumWindowState);
  const theme = useTheme();

  const isPollingProps = (
    props: useHiroPollingProps | useStreamingProps | undefined,
  ): props is useHiroPollingProps => {
    return type === HiroUpdatingType.POLLING && props != null;
  };

  const isStreamingProps = (
    props: useHiroPollingProps | useStreamingProps | undefined,
  ): props is useStreamingProps => {
    return type === HiroUpdatingType.STREAMING && props != null;
  };

  const { getPollingSeriesData, prices } = useHiroPolling(
    type === HiroUpdatingType.POLLING,
    isPollingProps(props) ? props : undefined,
  );

  const {
    getRolledCandles,
    getHistogramData: getStreamingHistogramData,
    getPrices: getStreamingPrices,
  } = useStreaming(
    type === HiroUpdatingType.STREAMING,
    isStreamingProps(props) ? props : undefined,
  );

  const getSeriesData = (lense: HiroLense, optionType: HiroOptionType) => {
    if (type === HiroUpdatingType.POLLING) {
      return getPollingSeriesData(lense, optionType);
    }

    return getRolledCandles(lense, optionType, rollingSeconds);
  };

  const getHistogramData = (lense: HiroLense) => {
    if (type === HiroUpdatingType.POLLING) {
      return undefined;
    }

    return getStreamingHistogramData(lense);
  };

  const getPrices = () => {
    if (type === HiroUpdatingType.POLLING) {
      return prices;
    }

    return getStreamingPrices() ?? [];
  };

  const getSeriesExtraOpts = (lense: HiroLense, optionType: HiroOptionType) => {
    if (type === HiroUpdatingType.POLLING) {
      return { ...SERIES_DEFAULT_OPTS };
    }

    const colors = theme.palette.hiro.lenses[lense];
    let colorForOptionType = colors.total;
    if (optionType === HiroOptionType.P) {
      colorForOptionType = colors.put;
    } else if (optionType === HiroOptionType.C) {
      colorForOptionType = colors.call;
    }

    const downColor =
      lense === HiroLense.All && optionType === HiroOptionType.TOT
        ? CANDLE_RED_COLOR
        : colorForOptionType;

    return {
      borderUpColor: colorForOptionType,
      upColor: colorForOptionType,
      borderDownColor: downColor,
      downColor,
      ...CANDLE_DEFAULT_SERIES_OPTS,
    };
  };

  return { getSeriesData, getPrices, getHistogramData, getSeriesExtraOpts };
};

export default useHiroUpdating;
